import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  Upload,
  Plus,
  ChevronUp,
  ChevronDown,
  Trash2,
  Loader2,
  GripVertical,
  Settings,
  RotateCw,
  Pencil,
  Copy,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import {
  LPComponent,
  LPComponentType,
  LP_COMPONENT_PALETTE,
} from './lpComponentTypes';
import LPComponentRenderer, { BrandTheme, BrandLogoOption } from './LPComponentRenderer';
import LPComponentBrowser from './LPComponentBrowser';
import LPPropertiesPanel from './LPPropertiesPanel';
import LPInlineFloatingToolbar from './LPInlineFloatingToolbar';
import { apiClient, regenerateLandingPageSection } from '../../services/api';
import LPRegenerateSectionPopover from './LPRegenerateSectionPopover';
import './LandingPageEditor.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

/** Build an @font-face block for the brand's primary font so it loads on
 *  preview / published pages. Without this the CSS family name resolves to
 *  the system sans fallback for visitors who don't have the font installed.
 *  Returns '' when no font URL is configured. */
const buildBrandFontFaceCss = (theme: BrandTheme): string => {
  const url = theme.fontUrl;
  const family = theme.fontFamily;
  if (!url || !family || family === 'inherit') return '';
  const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
  const format =
    ext === 'woff2' ? 'woff2' :
    ext === 'woff'  ? 'woff'  :
    ext === 'otf'   ? 'opentype' :
                       'truetype';
  return `@font-face{font-family:'${family}';src:url('${url}') format('${format}');font-display:swap}`;
};

// ── Props ───────────────────────────────────────────────────────────────────

interface LandingPageEditorProps {
  landingPageId: string;
}

// ── Section wrapper ─────────────────────────────────────────────────────────

const LP_REGEN_SUPPORTED_TYPES = new Set([
  'lp_hero',
  'lp_features',
  'lp_services',
  'lp_testimonials',
  'lp_faq',
  'lp_text',
  'lp_effect',
]);

interface SectionWrapperProps {
  component: LPComponent;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  onClick: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRegenerate: (componentId: string, instruction: string) => Promise<void>;
  onPropsChange: (id: string, newProps: Record<string, any>) => void;
  dragHandleProps?: Record<string, any>;
  brandTheme?: BrandTheme;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({
  component,
  isSelected,
  isFirst,
  isLast,
  onClick,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onRegenerate,
  onPropsChange,
  dragHandleProps,
  brandTheme,
}) => {
  const [regenOpen, setRegenOpen] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Remove this section from the page?')) {
      onDelete();
    }
  };

  const handleRegenerate = async (componentId: string, instruction: string) => {
    await onRegenerate(componentId, instruction);
    setRegenOpen(false);
  };

  const supportsRegen = LP_REGEN_SUPPORTED_TYPES.has(component.type);

  return (
    <div
      className={`lp-section-wrapper${isSelected ? ' selected' : ''}`}
      onClick={onClick}
      data-section-id={component.id}
    >
      <div className="lp-section-controls" onClick={(e) => e.stopPropagation()}>
        <span
          {...(dragHandleProps ?? {})}
          className="lp-section-ctrl-btn"
          title="Drag to reorder"
          style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center' }}
        >
          <GripVertical size={13} />
        </span>
        <button
          className="lp-section-ctrl-btn"
          title="Move up"
          disabled={isFirst}
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
        >
          <ChevronUp size={13} />
        </button>
        <button
          className="lp-section-ctrl-btn"
          title="Move down"
          disabled={isLast}
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
        >
          <ChevronDown size={13} />
        </button>
        {supportsRegen && (
          <button
            className="lp-section-ctrl-btn"
            title="Regenerate with AI"
            onClick={(e) => { e.stopPropagation(); setRegenOpen(prev => !prev); }}
          >
            <RotateCw size={13} />
          </button>
        )}
        <button
          className="lp-section-ctrl-btn"
          title="Duplicate section"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
        >
          <Copy size={13} />
        </button>
        <button
          className="lp-section-ctrl-btn danger"
          title="Delete section"
          onClick={handleDelete}
        >
          <Trash2 size={13} />
        </button>
      </div>
      {supportsRegen && regenOpen && (
        <LPRegenerateSectionPopover
          componentId={component.id}
          onRegenerate={handleRegenerate}
          onCancel={() => setRegenOpen(false)}
        />
      )}

      <LPComponentRenderer component={component} isSelected={isSelected} brandTheme={brandTheme} onPropsChange={onPropsChange} />
    </div>
  );
};

// ── Main editor ─────────────────────────────────────────────────────────────

const LandingPageEditor: React.FC<LandingPageEditorProps> = ({
  landingPageId,
}) => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [components, setComponents] = useState<LPComponent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageTitle, setPageTitle] = useState('Landing Page');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [pageSettings, setPageSettings] = useState<{ buttonRadius: 'pill' | 'rounded' | 'square' }>({ buttonRadius: 'pill' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [brandTheme, setBrandTheme] = useState<BrandTheme>({
    primaryColor: '#2177FF',
    secondaryColor: '#1EACA6',
    fontFamily: 'inherit',
    logoUrl: '',
    companyName: '',
  });
  // Template constraints (set from theme_config at load time)
  const [lockedLayout, setLockedLayout] = useState(false);
  const [maxComponents, setMaxComponents] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Load on mount ──────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const [pageRes, compRes] = await Promise.all([
          apiClient.get(`/landing-pages/${landingPageId}`),
          apiClient.get(`/landing-pages/${landingPageId}/components`),
        ]);
        setPageTitle(pageRes.data.title ?? 'Landing Page');

        const pid = pageRes.data.project_id;
        if (pid && pid !== 'unassigned') setProjectId(pid);

        // Load brand guide — theme_config may already have cached brand data (for pages
        // generated after March 2026); fall back to brand-guide API for older pages.
        const themeConfig = pageRes.data.theme_config || {};

        // Template constraints
        if (themeConfig.locked_layout) setLockedLayout(true);
        if (themeConfig.max_components) setMaxComponents(themeConfig.max_components);

        let theme: BrandTheme = {
          primaryColor: themeConfig.brand_primary_color || themeConfig.primary_color || '#2177FF',
          secondaryColor: themeConfig.brand_secondary_color || '#1EACA6',
          accentColor: themeConfig.brand_accent_color || themeConfig.accent_color || undefined,
          fontFamily: themeConfig.brand_primary_font || 'inherit',
          logoUrl: themeConfig.logo_url || '',
          companyName: themeConfig.company_name || '',
        };
        // Contact info from theme_config (cached at generation time)
        let contactInfo = {
          phone: themeConfig.phone || '',
          email: themeConfig.email || '',
          address: themeConfig.address || '',
          opening_hours: themeConfig.opening_hours || '',
        };
        if (pid && pid !== 'unassigned') {
          try {
            // Try default brand guide first, then fall back to first available
            let bg: any = null;
            try {
              const brandRes = await apiClient.get(`/brand-guides/project/${pid}/default`);
              bg = brandRes.data;
            } catch {
              const listRes = await apiClient.get(`/brand-guides/project/${pid}`);
              if (listRes.data && listRes.data.length > 0) {
                bg = listRes.data[0];
              }
            }
            if (bg) {
              // Collect all available logo variants
              const logoOptions: BrandLogoOption[] = [];
              if (bg.logo_url) logoOptions.push({ url: bg.logo_url, label: 'Main Logo' });
              if (bg.logo_dark_bg_url) logoOptions.push({ url: bg.logo_dark_bg_url, label: 'Dark Background' });
              if (bg.logo_monochrome_black_url) logoOptions.push({ url: bg.logo_monochrome_black_url, label: 'Monochrome Black' });
              if (bg.logo_monochrome_white_url) logoOptions.push({ url: bg.logo_monochrome_white_url, label: 'Monochrome White' });
              theme = {
                primaryColor: bg.primary_color || theme.primaryColor,
                secondaryColor: bg.secondary_color || theme.secondaryColor,
                accentColor: bg.accent_color || theme.accentColor,
                fontFamily: bg.primary_font || bg.secondary_font || theme.fontFamily,
                // Match the family above — if we fell back from primary to secondary,
                // pick the matching url too. Both can be empty (Google-Fonts style
                // family names work without a file URL on systems that already have them).
                fontUrl: bg.primary_font
                  ? bg.primary_font_url
                  : bg.secondary_font
                    ? bg.secondary_font_url
                    : undefined,
                logoUrl: bg.logo_url || theme.logoUrl,
                companyName: bg.brand_name || theme.companyName,
                logoOptions: logoOptions.length > 0 ? logoOptions : undefined,
              };
            }
          } catch {
            // Use theme_config fallback already set above
          }
        }
        setBrandTheme(theme);

        // Register the brand font in the running document so the editor canvas
        // displays it. Without this the family name falls back to system sans
        // for any user who hasn't visited the Brand Portal in the same session.
        // Browser dedupes FontFace by family — safe to call repeatedly.
        if (theme.fontUrl && theme.fontFamily && theme.fontFamily !== 'inherit' &&
            typeof document !== 'undefined' && 'fonts' in document &&
            typeof (window as any).FontFace === 'function') {
          try {
            const face = new FontFace(theme.fontFamily, `url(${theme.fontUrl})`);
            face.load().then(loaded => (document as any).fonts.add(loaded)).catch(() => {});
          } catch { /* non-fatal */ }
        }

        // Load components, auto-add header/footer if missing
        let comps: LPComponent[] = compRes.data?.components ?? [];

        // Auto-populate header company name and logo from brand guide
        const hasHeader = comps.some(c => c.type === 'lp_header');
        const hasFooter = comps.some(c => c.type === 'lp_footer');
        let changed = false;

        if (!hasHeader) {
          const headerPalette = LP_COMPONENT_PALETTE.find(p => p.type === 'lp_header')!;
          const headerComp: LPComponent = {
            id: `lp_header_${Date.now()}`,
            type: 'lp_header',
            props: {
              ...headerPalette.defaultProps,
              logo_url: theme.logoUrl,
              company_name: theme.companyName || 'Your Company',
            },
          };
          comps = [headerComp, ...comps];
          changed = true;
        }
        if (!hasFooter) {
          const footerPalette = LP_COMPONENT_PALETTE.find(p => p.type === 'lp_footer')!;
          const footerComp: LPComponent = {
            id: `lp_footer_${Date.now()}`,
            type: 'lp_footer',
            props: {
              ...footerPalette.defaultProps,
              logo_url: theme.logoUrl,
              company_name: theme.companyName || 'Your Company',
              copyright: `© ${new Date().getFullYear()} ${theme.companyName || 'Your Company'}. All rights reserved.`,
              phone: contactInfo.phone,
              email: contactInfo.email,
              address: contactInfo.address,
              opening_hours: contactInfo.opening_hours,
            },
          };
          comps = [...comps, footerComp];
          changed = true;
        }

        setComponents(comps);
        // Load page-level settings from hero component props
        const heroComp = comps.find(c => c.type === 'lp_hero');
        if (heroComp?.props?._page_button_radius) {
          setPageSettings(s => ({ ...s, buttonRadius: heroComp.props._page_button_radius }));
        }
        if (changed) setIsDirty(true);  // auto-save the header/footer additions
      } catch (err: any) {
        setError(getApiError(err, 'Failed to load landing page. Please try again.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [landingPageId]);

  // ── Auto-save (debounced 2 s) ──────────────────────────────────────────────

  const saveComponents = useCallback(
    async (comps: LPComponent[]) => {
      setSaving(true);
      try {
        await apiClient.patch(`/landing-pages/${landingPageId}/components`, {
          components: comps,
        });
        setIsDirty(false);
        setSaveError(null);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          // User has view-only access — lock the editor and revert local changes
          setLockedLayout(true);
          setSaveError('View-only access: you cannot edit this landing page. A team admin needs to set the project share permission to "edit" in Team Settings.');
          try {
            const res = await apiClient.get(`/landing-pages/${landingPageId}/components`);
            setComponents(res.data?.components ?? []);
          } catch { /* ignore reload failure */ }
          setIsDirty(false);
        }
        // Other errors: silent failure — user can retry with the Save button
      } finally {
        setSaving(false);
      }
    },
    [landingPageId]
  );

  useEffect(() => {
    if (!isDirty) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveComponents(components);
    }, 2000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [components, isDirty, saveComponents]);

  // ── Rename page title (persists to the LP row, not the components autosave) ──
  const startTitleEdit = useCallback(() => {
    if (lockedLayout) return;
    setTitleDraft(pageTitle);
    setEditingTitle(true);
  }, [lockedLayout, pageTitle]);

  const commitTitleEdit = useCallback(async () => {
    const next = titleDraft.trim();
    // No change or emptied → just exit edit mode without a write
    if (!next || next === pageTitle) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      // Title only — slug is intentionally left unchanged so published URLs don't break
      await apiClient.put(`/landing-pages/${landingPageId}`, { title: next });
      setPageTitle(next);
      setEditingTitle(false);
      setSaveError(null);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setLockedLayout(true);
        setSaveError('View-only access: you cannot edit this landing page. A team admin needs to set the project share permission to "edit" in Team Settings.');
        setEditingTitle(false);
      } else {
        setSaveError(getApiError(err, 'Failed to rename landing page. Please try again.'));
      }
    } finally {
      setSavingTitle(false);
    }
  }, [titleDraft, pageTitle, landingPageId]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const markDirty = () => setIsDirty(true);

  // Inject page-level button radius CSS
  useEffect(() => {
    const radiusMap = { pill: '9999px', rounded: '8px', square: '0px' };
    const r = radiusMap[pageSettings.buttonRadius] || '9999px';
    const id = 'lp-page-settings-css';
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = `.lp-btn { border-radius: ${r} !important; }`;
  }, [pageSettings.buttonRadius]);

  const addComponent = (type: LPComponentType) => {
    if (lockedLayout) return;
    if (maxComponents && components.length >= maxComponents) return;
    const palette = LP_COMPONENT_PALETTE.find((p) => p.type === type);
    if (!palette) return;
    const newComp: LPComponent = {
      id: `${type}_${Date.now()}`,
      type,
      props: { ...palette.defaultProps },
    };
    setComponents((prev) => {
      const next = [...prev, newComp];
      return next;
    });
    setSelectedId(newComp.id);
    markDirty();
  };

  const moveUp = (id: string) => {
    setComponents((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    markDirty();
  };

  const moveDown = (id: string) => {
    setComponents((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    markDirty();
  };

  const duplicateComponent = (id: string) => {
    if (lockedLayout) return;
    if (maxComponents && components.length >= maxComponents) return;
    let newId: string | null = null;
    setComponents((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx < 0) return prev;
      const orig = prev[idx];
      newId = `${orig.type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      // Deep-clone props so the copy is fully independent. Clear unique bindings so
      // a duplicated lead-form/quiz isn't silently wired to the original's analytics.
      const props = JSON.parse(JSON.stringify(orig.props ?? {}));
      delete props.form_id;
      delete props.quiz_id;
      const copy: LPComponent = { id: newId, type: orig.type, props };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    if (newId) setSelectedId(newId);
    markDirty();
  };

  const deleteComponent = (id: string) => {
    if (lockedLayout) return;
    setComponents((prev) => prev.filter((c) => c.id !== id));
    if (selectedId === id) setSelectedId(null);
    markDirty();
  };

  const handlePropsChange = (id: string, newProps: Record<string, any>) => {
    setComponents((prev) =>
      prev.map((c) => (c.id === id ? { ...c, props: newProps } : c))
    );
    markDirty();
  };

  const handleRegenerateSection = async (componentId: string, instruction: string): Promise<void> => {
    const comp = components.find(c => c.id === componentId);
    if (!comp) return;
    const res = await regenerateLandingPageSection(landingPageId, {
      component_id: componentId,
      component_type: comp.type,
      instruction: instruction || undefined,
      current_props: comp.props,
    });
    setComponents(prev => prev.map(c =>
      c.id === componentId
        ? { ...c, props: { ...c.props, ...res.props } }
        : c
    ));
    markDirty();
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    setComponents((prev) => {
      const next = [...prev];
      const [removed] = next.splice(result.source.index, 1);
      next.splice(result.destination!.index, 0, removed);
      return next;
    });
    markDirty();
  };

  const scrollToSection = (id: string) => {
    const el = canvasRef.current?.querySelector(
      `[data-section-id="${id}"]`
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setSelectedId(id);
  };

  // ── Save manually ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    await saveComponents(components);
  };

  // ── Clean HTML extraction (strips all editor scaffolding) ──────────────────

  const cleanEditorArtifacts = (clone: HTMLElement, origin?: string): string => {

    // Remove editor-only UI elements
    clone.querySelectorAll('.lp-section-controls').forEach(el => el.remove());
    clone.querySelectorAll('button[title="Change logo"]').forEach(el => el.remove());

    // Unwrap .lp-section-wrapper (keep only the rendered component inside)
    clone.querySelectorAll('.lp-section-wrapper').forEach(wrapper => {
      const frag = document.createDocumentFragment();
      Array.from(wrapper.childNodes).forEach(child => frag.appendChild(child));
      wrapper.replaceWith(frag);
    });

    // Unwrap DnD draggable divs
    clone.querySelectorAll('[data-rfd-draggable-id]').forEach(el => {
      const frag = document.createDocumentFragment();
      Array.from(el.childNodes).forEach(child => frag.appendChild(child));
      el.replaceWith(frag);
    });

    // Remove DnD placeholder (empty div inserted by Droppable)
    clone.querySelectorAll('[data-rfd-placeholder-context-id]').forEach(el => el.remove());

    // Strip editing attributes
    clone.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
    clone.querySelectorAll('[readonly]').forEach(el => el.removeAttribute('readonly'));
    clone.querySelectorAll('[data-section-id]').forEach(el => el.removeAttribute('data-section-id'));
    clone.querySelectorAll('[data-rfd-droppable-id]').forEach(el => {
      el.removeAttribute('data-rfd-droppable-id');
      el.removeAttribute('data-rfd-droppable-context-id');
    });
    // Remove inline-editing outline hints
    clone.querySelectorAll('[data-lp-editable]').forEach(el => {
      el.removeAttribute('data-lp-editable');
      (el as HTMLElement).style.removeProperty('border');
      (el as HTMLElement).style.removeProperty('cursor');
      el.removeAttribute('title');
    });
    // Remove editor-only chrome (e.g. the "upload an image" hint on effect sections).
    clone.querySelectorAll('[data-lp-editor-only]').forEach(el => el.remove());

    // Swap live RevFX effect sections for <aime-effect> custom elements. Once the
    // DOM is serialized the live effect canvas is a dead snapshot, so we keep ONLY
    // the overlay (the stable .lps-effect-overlay subtree) as the element's
    // children; the published runtime (/widgets/aime-effects.js) re-mounts the
    // effect from the effect-id/props attributes and renders the overlay on top.
    // The wrapper's layout styles (position:relative; min-height; overflow) are
    // preserved so the overlay still layers correctly on the static page.
    clone.querySelectorAll('[data-aime-effect]').forEach(node => {
      const effectId = node.getAttribute('data-aime-effect') || '';
      const propsJson = node.getAttribute('data-effect-props') || '{}';
      const overlay = node.querySelector('.lps-effect-overlay');
      const wrapperStyle = (node as HTMLElement).getAttribute('style') || '';
      const el = document.createElement('aime-effect');
      el.setAttribute('effect-id', effectId);
      el.setAttribute('props', propsJson);
      if (wrapperStyle) el.setAttribute('style', wrapperStyle);
      el.innerHTML = overlay ? overlay.outerHTML : '';
      node.replaceWith(el);
    });

    let html = clone.innerHTML;
    if (origin) {
      html = html
        .replace(/src="\/assets\//g, `src="${origin}/assets/`)
        .replace(/src='\/assets\//g, `src='${origin}/assets/`)
        .replace(/href="\/assets\//g, `href="${origin}/assets/`);
    }
    return html;
  };

  const getCleanCanvasHTML = (origin?: string): string => {
    if (!canvasRef.current) return '';
    return cleanEditorArtifacts(canvasRef.current.cloneNode(true) as HTMLElement, origin);
  };

  // ── Preview ────────────────────────────────────────────────────────────────

  const handlePreview = async () => {
    if (!canvasRef.current) return;
    const origin = window.location.origin;
    const innerHTML = getCleanCanvasHTML(origin);
    const btnRadiusMap: Record<string, string> = { pill: '9999px', rounded: '8px', square: '0px' };
    const btnR = btnRadiusMap[pageSettings.buttonRadius] || '9999px';
    const btnHoverCss = `html{scroll-behavior:smooth}*{box-sizing:border-box}.lp-btn{transition:filter .18s ease,transform .18s ease;cursor:pointer;border-radius:${btnR}!important}.lp-btn:hover{filter:brightness(.88);transform:translateY(-1px) scale(1.02)}.lp-btn:active{filter:brightness(.78);transform:translateY(0) scale(.99)}.lp-btn-ghost{transition:background .18s ease,transform .18s ease;cursor:pointer}.lp-btn-ghost:hover{background:rgba(255,255,255,.28)!important;transform:translateY(-1px)}.lp-btn-ghost:active{transform:translateY(0)}@media(max-width:768px){body{overflow-x:hidden}.lps{padding:32px 16px!important}.lps-inner{padding:0 16px!important}.lps-header-bar{padding:0 16px!important;min-height:60px!important}.lps-nav{display:none!important}.lps-phone{display:none!important}.lps-desktop-cta{display:none!important}.lps-hamburger{display:block!important}.lps-header img{height:32px!important}.lps-hero-split{padding:32px 16px!important}.lps-hero-grid{display:flex!important;flex-direction:column!important;gap:16px!important}.lps-hero-split .lps-hero-grid>div:first-child .lp-btn,.lps-hero-split .lps-hero-grid>div:first-child .lp-btn-ghost{display:none!important}#hero-form,#cta-form{margin:0 -16px!important;border-radius:0!important;padding:28px 20px!important;box-shadow:none!important;width:calc(100% + 32px)!important}.lps-grid{grid-template-columns:1fr!important;gap:16px!important}.lps-features .lps-grid{grid-template-columns:repeat(2,1fr)!important;gap:12px!important}.lps-stats-row{display:grid!important;grid-template-columns:1fr 1fr!important;gap:16px 24px!important}.lps-process-row{flex-direction:column!important;align-items:stretch!important;gap:0!important}.lps-process-row>div{flex-direction:row!important;align-items:center!important;max-width:100%!important;gap:14px!important;padding:14px 0!important;border-bottom:1px solid #e2e8f0}.lps-process-row>div:last-child{border-bottom:none}.lps-process-row>div>div[style*="border-radius: 50%"]{flex-shrink:0!important;width:36px!important;height:36px!important;font-size:.85rem!important}.lps-process-row>div>div[style*="position: absolute"]{display:none!important}.lps-process-text{flex:1!important}.lps-process-text h4{text-align:left!important;margin:0 0 2px!important;font-size:.85rem!important}.lps-process-text p{text-align:left!important;margin:2px 0 0!important;font-size:.78rem!important}.lps-footer-grid{display:flex!important;flex-direction:column!important;gap:24px!important;padding:32px 16px!important}.lps-footer>div>div{text-align:left!important}h1{font-size:2rem!important}h2{font-size:1.4rem!important}.lps-stats h2,.lps-stats div[style*="2.8rem"]{font-size:2rem!important}aime-quiz{min-height:auto!important}.lps-effect-overlay{padding:0 20px!important}.lps-effect-overlay h2{font-size:1.7rem!important}}`;
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${pageTitle} — Preview</title>
  <link rel="stylesheet" href="${origin}/widgets/style.css">
  <style>${buildBrandFontFaceCss(brandTheme)}body{margin:0;font-family:${brandTheme.fontFamily && brandTheme.fontFamily !== 'inherit' ? `'${brandTheme.fontFamily}',` : ''}-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}${btnHoverCss}</style>
</head>
<body>
${innerHTML}
<script type="module" src="${origin}/widgets/aime-widgets.js"></script>
${innerHTML.includes('<aime-effect') ? `<script type="module" src="${origin}/widgets/aime-effects.js?v=${Date.now()}"></script>` : ''}
<script>(function(){document.querySelectorAll('[data-aime-timer]').forEach(function(t){var end=t.getAttribute('data-end');var redir=t.getAttribute('data-redirect');var msg=t.getAttribute('data-expired-msg')||'This offer has expired.';if(!end)return;var endMs=new Date(end).getTime();var display=t.querySelector('[data-aime-timer-display]');var expiredEl=t.querySelector('[data-aime-timer-expired]');var dEl=t.querySelector('[data-aime-timer-d]');var hEl=t.querySelector('[data-aime-timer-h]');var mEl=t.querySelector('[data-aime-timer-m]');var sEl=t.querySelector('[data-aime-timer-s]');function pad(n){return String(n).padStart(2,'0')}function buildRedirect(base){var ref=document.referrer;if(!ref)return base;return base+(base.indexOf('?')>=0?'&':'?')+'from='+encodeURIComponent(ref)}function tick(){var diff=Math.max(0,Math.floor((endMs-Date.now())/1000));if(diff<=0){if(redir){window.location.href=buildRedirect(redir);return}if(display)display.style.display='none';if(!expiredEl){var p=document.createElement('p');p.style.cssText='font-size:1.1rem;font-style:italic;color:rgba(255,255,255,0.9)';p.textContent=msg;t.appendChild(p)}else{expiredEl.style.display=''}return}var d=Math.floor(diff/86400);var h=Math.floor((diff%86400)/3600);var m=Math.floor((diff%3600)/60);var s=diff%60;if(dEl)dEl.textContent=pad(d);if(hEl)hEl.textContent=pad(h);if(mEl)mEl.textContent=pad(m);if(sEl)sEl.textContent=pad(s)}if(expiredEl)expiredEl.style.display='none';tick();setInterval(tick,1000)})})()</script>
<script>(function(){var ep=document.querySelector('[data-aime-exit-popup]');if(!ep)return;var once=ep.getAttribute('data-once')==='1';var badge=ep.querySelector('[data-aime-exit-badge]');if(badge)badge.remove();var hint=ep.querySelector('[data-aime-exit-hint]');if(hint)hint.remove();var card=ep.querySelector('[data-aime-exit-card]');if(card){card.style.border='none';card.style.borderRadius='16px'}var dismiss=ep.querySelector('button');if(dismiss){dismiss.disabled=false;dismiss.style.cursor='pointer'}ep.style.cssText='display:none;position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,0.6);backdrop-filter:blur(4px);align-items:center;justify-content:center;padding:24px';var shown=false;function show(){if(shown)return;if(once&&sessionStorage.getItem('aime_exit_shown'))return;shown=true;ep.style.display='flex';if(once)sessionStorage.setItem('aime_exit_shown','1')}function hide(){ep.style.display='none'}if(dismiss)dismiss.addEventListener('click',hide);ep.addEventListener('click',function(e){if(e.target===ep)hide()});document.addEventListener('mouseleave',function(e){if(e.clientY<0)show()})})()</script>
<script>(function(){document.querySelectorAll('[data-aime-roi]').forEach(function(c){var formula=c.getAttribute('data-formula');if(!formula)return;var inputs=JSON.parse(c.getAttribute('data-inputs')||'[]');var prefix=c.getAttribute('data-output-prefix')||'';var suffix=c.getAttribute('data-output-suffix')||'';var resultEl=c.querySelector('[data-aime-roi-result]');if(!resultEl)return;var vals={};inputs.forEach(function(inp){vals[inp.id]=inp.default||0});function fmt(n){if(isNaN(n)||!isFinite(n))return'\u2014';return Math.abs(n)>=1000?n.toLocaleString('en-US',{maximumFractionDigits:0}):n.toFixed(2)}function calc(){try{var names=inputs.map(function(i){return i.id});var args=inputs.map(function(i){return vals[i.id]});var fn=new Function(names.join(','),'return ('+formula+')');var r=fn.apply(null,args);resultEl.textContent=prefix+fmt(r)+suffix}catch(e){resultEl.textContent='\u2014'}}inputs.forEach(function(inp){var el=c.querySelector('[data-aime-roi-input="'+inp.id+'"]');var lbl=c.querySelector('[data-aime-roi-label="'+inp.id+'"]');if(!el)return;el.addEventListener('input',function(){vals[inp.id]=Number(el.value);if(lbl)lbl.textContent='('+inp.prefix+el.value+inp.suffix+')';calc()})});calc()})})()</script>
<script>(function(){function focusFirstInput(container){if(!container)return;var inp=container.querySelector('input:not([type=hidden])');if(!inp)inp=container.querySelector('textarea');if(inp){setTimeout(function(){inp.readOnly=false;inp.focus()},300)}}var lf=document.getElementById('lead-form');var hf=document.getElementById('hero-form');var cf=document.getElementById('cta-form');var hasQuiz=document.querySelector('aime-quiz')||document.querySelector('iframe[src*="quiz-embed"]');if(hasQuiz&&lf){lf.style.display='none'}window.aimeQuizComplete=function(){setTimeout(function(){if(lf&&lf.style.display==='none')lf.style.display='';var quiz=document.querySelector('aime-quiz')||document.querySelector('iframe[src*="quiz-embed"]');var quizBottom=quiz?quiz.getBoundingClientRect().bottom+window.scrollY:0;var candidates=[lf,hf,cf].filter(Boolean);var below=candidates.filter(function(el){return el.getBoundingClientRect().top+window.scrollY>quizBottom});var target=lf||below[0]||candidates[0];if(target){target.scrollIntoView({behavior:'smooth',block:'start'});focusFirstInput(target)}},5000)};window.addEventListener('message',function(e){if(!e.data||typeof e.data!=='object')return;if(e.data.type==='aime-quiz-resize'){var frames=document.querySelectorAll('iframe');frames.forEach(function(f){if(f.src&&f.src.indexOf(e.data.quizId)>=0){f.style.height=e.data.height+'px'}})};if(e.data.type==='aime-quiz-complete'){window.aimeQuizComplete()}});document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(!a)return;var href=a.getAttribute('href');if(href==='#lead-form'||href==='#hero-form'||href==='#contact'){e.preventDefault();var target=hf||cf||lf;if(target){if(target.style.display==='none')target.style.display='';target.scrollIntoView({behavior:'smooth',block:'start'});focusFirstInput(target)}}})})()</script>
<script>(function(){var wrap=document.querySelector('[data-aime-testimonials]');if(!wrap)return;var cards=Array.prototype.slice.call(wrap.querySelectorAll('[data-testimonial]'));if(cards.length<=1)return;var idx=0;var primary=getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim()||'#2177FF';var dotWrap=wrap.querySelector('[data-aime-testimonial-dots]');function renderDots(){if(!dotWrap)return;dotWrap.innerHTML='';cards.forEach(function(_,i){var d=document.createElement('div');d.style.cssText='height:8px;border-radius:9999px;cursor:pointer;transition:width .3s,background .3s;width:'+(i===idx?'24px':'8px')+';background:'+(i===idx?primary:'#cbd5e1');d.addEventListener('click',function(){goTo(i)});dotWrap.appendChild(d)})}function goTo(next){cards[idx].style.display='none';idx=next%cards.length;cards[idx].style.display='block';cards[idx].style.animation='none';void cards[idx].offsetHeight;cards[idx].style.animation='lp-testimonial-slide-in .45s ease';renderDots()}renderDots();setInterval(function(){goTo(idx+1)},5000)})()</script>
<script>(function(){var btn=document.querySelector('.lps-hamburger');var menu=document.querySelector('.lps-mobile-menu');if(!btn||!menu)return;var open=false;btn.addEventListener('click',function(){open=!open;menu.style.display=open?'flex':'none';btn.innerHTML=open?'<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>':'<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>'});menu.querySelectorAll('a').forEach(function(a){a.addEventListener('click',function(){open=false;menu.style.display='none';btn.innerHTML='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>'}})})()</script>
</body>
</html>`;
    try {
      const res = await fetch('/api/landing-pages/preview-temp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html }),
      });
      const { uid } = await res.json();
      window.open(`/api/landing-pages/preview-temp/${uid}`, '_blank');
    } catch {
      // Fallback to blob if server unavailable
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  // ── Publish ────────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!canvasRef.current) return;
    if (!window.confirm('Publish this landing page? It will be publicly accessible.')) return;

    setSaving(true);
    try {
      // Clone the canvas so we can post-process without mutating the live DOM
      const canvasClone = canvasRef.current.cloneNode(true) as HTMLElement;

      // Pre-fetch linked lead form data for components with form_id
      const linkedFormDataMap: Record<string, any> = {};
      const formFetchPromises = components
        .filter((c) => c.type === 'lp_lead_form' && c.props.form_id)
        .map(async (c) => {
          try {
            const data = await apiClient.get(`/lead-capture/forms/${c.props.form_id}`).then((r: any) => r.data);
            linkedFormDataMap[c.props.form_id] = data;
          } catch { /* form fetch failed — fall back to widget */ }
        });
      await Promise.all(formFetchPromises);

      // Replace each lp_lead_form section with form HTML
      for (const comp of components) {
        if (comp.type === 'lp_lead_form') {
          const sectionEl = canvasClone.querySelector(`[data-section-id="${comp.id}"]`);
          if (sectionEl) {
            // Match the React LeadFormRenderer (LPComponentRenderer.tsx:1202),
            // which uses props.button_color || brand secondary. Earlier this
            // used primaryColor which produced the wrong colour on publish.
            const buttonColor = comp.props.button_color || brandTheme?.secondaryColor || '#1EACA6';
            // Match the React LeadFormRenderer structure: <h2> title (1.8rem, 700, #0f172a)
            // and centred <p> subtitle, both sit inside the .lps-inner wrap so the section
            // inherits the same section/inner CSS as every other published section.
            const title = comp.props.title ? `<h2 style="text-align:center;margin-bottom:8px;font-size:1.8rem;font-weight:700;color:#0f172a">${comp.props.title}</h2>` : '';
            const subtitle = comp.props.subtitle ? `<p style="text-align:center;color:#64748b;margin-bottom:32px">${comp.props.subtitle}</p>` : '';
            // Section style: prefer the existing React-rendered cssText (carries
            // background, padding 60px 0, custom font-family etc.). Fall back to
            // the same defaults the React renderer uses — NOT padding 60px 48px,
            // because horizontal padding now lives on the inner wrap below.
            const sectionStyle = (sectionEl as HTMLElement).style?.cssText || '';
            const replacement = document.createElement('div');
            replacement.className = 'lps lps-lead-form';
            replacement.setAttribute('id', 'lead-form');
            replacement.setAttribute('data-section-id', comp.id);
            replacement.style.cssText = sectionStyle || 'background:#ffffff;padding:60px 0;';

            const formId = comp.props.form_id;
            const linkedData = formId ? linkedFormDataMap[formId] : null;

            if (linkedData && linkedData.fields && linkedData.fields.length > 0) {
              // Build inline HTML form from linked Lead Capture Form fields
              const formFieldsHtml = linkedData.fields.map((f: any) => {
                const fid = f.id || '';
                const ftype = f.type || 'text';
                const label = f.label || '';
                const placeholder = f.placeholder || '';
                const required = f.required ? 'required' : '';
                const reqStar = f.required ? ' *' : '';

                if (ftype === 'textarea') {
                  return `<div style="margin-bottom:14px"><label style="display:block;margin-bottom:4px;font-size:14px;font-weight:500">${label}${reqStar}</label><textarea id="${fid}" name="${fid}" placeholder="${placeholder}" ${required} rows="3" style="width:100%;padding:10px 14px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;background:#f8fafc;box-sizing:border-box;font-family:inherit;resize:vertical"></textarea></div>`;
                } else if (ftype === 'select') {
                  const options = (f.options || []).map((o: string) => `<option value="${o}">${o}</option>`).join('');
                  return `<div style="margin-bottom:14px"><label style="display:block;margin-bottom:4px;font-size:14px;font-weight:500">${label}${reqStar}</label><select id="${fid}" name="${fid}" ${required} style="width:100%;padding:10px 14px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;background:#f8fafc;box-sizing:border-box"><option value="">-- Select --</option>${options}</select></div>`;
                } else {
                  return `<div style="margin-bottom:14px"><label style="display:block;margin-bottom:4px;font-size:14px;font-weight:500">${label}${reqStar}</label><input type="${ftype}" id="${fid}" name="${fid}" placeholder="${placeholder}" ${required} style="width:100%;padding:10px 14px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;background:#f8fafc;box-sizing:border-box"/></div>`;
                }
              }).join('\n');

              const submitText = linkedData.submit_button_text || comp.props.submit_label || 'Submit';
              const successMsg = (linkedData.success_message || 'Thank you! We\'ll be in touch.').replace(/"/g, '&quot;');

              replacement.innerHTML = `<div class="lps-inner" style="max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;padding:0 48px;display:flex;flex-direction:column;align-items:center">${title}${subtitle}
<form id="aime-lcf-${formId}" style="width:100%;max-width:480px">
  ${formFieldsHtml}
  <button type="submit" style="width:100%;padding:12px;background:${buttonColor};color:#fff;border:none;border-radius:9999px;font-weight:700;font-size:1rem;cursor:pointer;margin-top:6px">${submitText}</button>
</form>
<p id="aime-lcf-success-${formId}" style="display:none;color:#22c55e;font-weight:500;margin-top:12px;text-align:center;width:100%;max-width:480px">${successMsg}</p>
<p id="aime-lcf-error-${formId}" style="display:none;color:#ef4444;font-weight:500;margin-top:12px;text-align:center;width:100%;max-width:480px"></p>
<script>
(function(){
  var form=document.getElementById('aime-lcf-${formId}');
  if(!form)return;
  form.addEventListener('submit',async function(e){
    e.preventDefault();
    var errEl=document.getElementById('aime-lcf-error-${formId}');
    var successEl=document.getElementById('aime-lcf-success-${formId}');
    if(errEl)errEl.style.display='none';
    var data={};
    Array.from(form.elements).forEach(function(el){if(el.name)data[el.name]=el.value});
    try{
      var resp=await fetch('/api/lead-capture/forms/${formId}/submit',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({data:data})
      });
      if(resp.ok){
        form.style.display='none';
        if(successEl)successEl.style.display='block';
      }else{
        var body=await resp.json().catch(function(){return{}});
        if(errEl){errEl.textContent=body.detail||'Submission failed. Please try again.';errEl.style.display='block'}
      }
    }catch(err){
      if(errEl){errEl.textContent='Network error. Please try again.';errEl.style.display='block'}
    }
  });
})();
<\/script></div>`;
            } else {
              // Fallback: use the aime-lead-form web component. Wrap in the same
              // .lps-inner structure as the linked-form branch for visual parity.
              const fields = JSON.stringify(comp.props.fields || ['name', 'email', 'phone']);
              replacement.innerHTML = `<div class="lps-inner" style="max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;padding:0 48px;display:flex;flex-direction:column;align-items:center">${title}${subtitle}<aime-lead-form project-id="${projectId || ''}" landing-page-id="${landingPageId}" fields='${fields}' theme="professional" primary-color="${buttonColor}"></aime-lead-form></div>`;
            }
            sectionEl.replaceWith(replacement);
          }
        }
      }

      // Replace each GATED lp_whitepaper section with a real aime-lead-form
      // web component carrying component-id, so landing_page_routes.submit_lead
      // can resolve the product server-side from LandingPage.components (never
      // from the request body — see specs/completed/whitepaper-module.md
      // Phase 4, Known Risk #2). Ungated whitepapers need no splice: the React
      // WhitepaperRenderer already emits a real, functional <a href> anchor to
      // the public download route, which survives DOM serialisation as-is.
      for (const comp of components) {
        if (comp.type === 'lp_whitepaper' && comp.props.gated !== false && comp.props.product_id) {
          const sectionEl = canvasClone.querySelector(`[data-section-id="${comp.id}"]`);
          if (sectionEl) {
            const buttonColor = brandTheme?.secondaryColor || '#1EACA6';
            const heading = comp.props.heading
              ? `<h2 style="text-align:center;margin-bottom:8px;font-size:2rem;font-weight:800;color:#0f172a">${comp.props.heading}</h2>`
              : '';
            const subheading = comp.props.subheading
              ? `<p style="text-align:center;color:#64748b;margin-bottom:32px;max-width:560px">${comp.props.subheading}</p>`
              : '';
            const sectionStyle = (sectionEl as HTMLElement).style?.cssText || '';
            const replacement = document.createElement('div');
            replacement.className = 'lps lps-whitepaper';
            replacement.setAttribute('id', 'whitepaper');
            replacement.setAttribute('data-section-id', comp.id);
            replacement.style.cssText = sectionStyle || 'background:#ffffff;padding:60px 0;';

            const submitTextAttr = comp.props.button_label
              ? ` submit-text="${String(comp.props.button_label).replace(/"/g, '&quot;')}"`
              : '';

            replacement.innerHTML = `<div class="lps-inner" style="max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;padding:0 48px;display:flex;flex-direction:column;align-items:center">${heading}${subheading}<aime-lead-form project-id="${projectId || ''}" landing-page-id="${landingPageId}" component-id="${comp.id}" fields='["name","email"]' theme="professional" primary-color="${buttonColor}"${submitTextAttr}></aime-lead-form></div>`;
            sectionEl.replaceWith(replacement);
          }
        }
      }

      // Replace hero-form placeholder with a real aime-lead-form web component
      const heroFormEl = canvasClone.querySelector('#hero-form');
      if (heroFormEl) {
        const heroComp = components.find(c => c.type === 'lp_hero');
        const buttonColor = heroComp?.props?.button_color || brandTheme?.secondaryColor || '#1EACA6';
        const heroForm = document.createElement('div');
        heroForm.setAttribute('id', 'hero-form');
        heroForm.style.cssText = (heroFormEl as HTMLElement).style?.cssText || 'background:rgba(255,255,255,0.97);border-radius:12px;padding:32px;box-shadow:0 8px 40px rgba(0,0,0,0.2)';
        const formTitle = heroComp?.props?.form_title || 'Book Your Free Consultation';
        // Pass the editor's form_submit_text to the widget so the published
        // button label matches what was configured. The widget also adds
        // `lp-btn` to the submit button's classList, so the global
        // .lp-btn{border-radius} rule applies pageSettings.buttonRadius
        // without any per-widget radius wiring.
        const submitTextAttr = heroComp?.props?.form_submit_text
          ? ` submit-text="${String(heroComp.props.form_submit_text).replace(/"/g, '&quot;')}"`
          : '';
        heroForm.innerHTML = `<h3 style="margin:0 0 20px;color:#0f172a;font-size:1.1rem;font-weight:700">${formTitle}</h3><aime-lead-form project-id="${projectId || ''}" landing-page-id="${landingPageId}" fields='["name","email","phone"]' theme="professional" primary-color="${buttonColor}"${submitTextAttr}></aime-lead-form>`;
        heroFormEl.replaceWith(heroForm);
      }

      let innerHTML = cleanEditorArtifacts(canvasClone);
      // Replace quiz embed iframes with inline <aime-quiz> elements for the published page
      const origin = window.location.origin;
      innerHTML = innerHTML.replace(
        /<iframe[^>]+src=["'][^"']*\/quiz-embed\/([^?'"]+)\?([^"']*)["'][^>]*><\/iframe>/g,
        (_match, quizId, queryString) => {
          const qp = new URLSearchParams(queryString.replace(/&amp;/g, '&'));
          const pc = qp.get('primary_color') || '#2177FF';
          const sc = qp.get('secondary_color') || '';
          const ac = qp.get('accent_color') || '';
          const ff = qp.get('font_family') || '';
          return `<aime-quiz quiz-id="${quizId}" primary-color="${pc}"${sc ? ` secondary-color="${sc}"` : ''}${ac ? ` accent-color="${ac}"` : ''}${ff ? ` font-family="${ff}"` : ''} on-complete="aimeQuizComplete" style="display:block;max-width:680px;margin:0 auto;min-height:500px"></aime-quiz>`;
        }
      );
      const widgetScript = `<script type="module" src="/widgets/aime-widgets.js"></script>`;
      // RevFX effects runtime — injected CONDITIONALLY (only when the page has at
      // least one effect section), unlike aime-widgets.js which ships on every
      // page. The asymmetry is intentional: aime-effects.js carries the heavier
      // RevFX runtime (and lazy-loads three.js for WebGL effects), so effect-free
      // pages must not download it. The gate is this publish-time DOM check, NOT a
      // backend needs_widgets-style flag.
      const effectsScript = innerHTML.includes('<aime-effect')
        ? `<script type="module" src="/widgets/aime-effects.js?v=${Date.now()}"></script>`
        : '';
      const btnRadiusMap: Record<string, string> = { pill: '9999px', rounded: '8px', square: '0px' };
      const btnR = btnRadiusMap[pageSettings.buttonRadius] || '9999px';
      const btnHoverCss = `<style>html{scroll-behavior:smooth}*{box-sizing:border-box}.lp-btn{transition:filter .18s ease,transform .18s ease,box-shadow .18s ease;cursor:pointer;border-radius:${btnR}!important}.lp-btn:hover{filter:brightness(.88);transform:translateY(-1px) scale(1.02)}.lp-btn:active{filter:brightness(.78);transform:translateY(0) scale(.99)}.lp-btn-ghost{transition:background .18s ease,transform .18s ease;cursor:pointer}.lp-btn-ghost:hover{background:rgba(255,255,255,.28)!important;transform:translateY(-1px)}.lp-btn-ghost:active{transform:translateY(0)}@media(max-width:768px){body{overflow-x:hidden}.lps{padding:32px 16px!important}.lps-inner{padding:0 16px!important}.lps-header-bar{padding:0 16px!important;min-height:60px!important}.lps-nav{display:none!important}.lps-phone{display:none!important}.lps-desktop-cta{display:none!important}.lps-hamburger{display:block!important}.lps-header img{height:32px!important}.lps-hero-split{padding:32px 16px!important}.lps-hero-grid{display:flex!important;flex-direction:column!important;gap:16px!important}.lps-hero-split .lps-hero-grid>div:first-child .lp-btn,.lps-hero-split .lps-hero-grid>div:first-child .lp-btn-ghost{display:none!important}#hero-form,#cta-form{margin:0 -16px!important;border-radius:0!important;padding:28px 20px!important;box-shadow:none!important;width:calc(100% + 32px)!important}.lps-grid{grid-template-columns:1fr!important;gap:16px!important}.lps-features .lps-grid{grid-template-columns:repeat(2,1fr)!important;gap:12px!important}.lps-stats-row{display:grid!important;grid-template-columns:1fr 1fr!important;gap:16px 24px!important}.lps-process-row{flex-direction:column!important;align-items:stretch!important;gap:0!important}.lps-process-row>div{flex-direction:row!important;align-items:center!important;max-width:100%!important;gap:14px!important;padding:14px 0!important;border-bottom:1px solid #e2e8f0}.lps-process-row>div:last-child{border-bottom:none}.lps-process-row>div>div[style*="border-radius: 50%"]{flex-shrink:0!important;width:36px!important;height:36px!important;font-size:.85rem!important}.lps-process-row>div>div[style*="position: absolute"]{display:none!important}.lps-process-text{flex:1!important}.lps-process-text h4{text-align:left!important;margin:0 0 2px!important;font-size:.85rem!important}.lps-process-text p{text-align:left!important;margin:2px 0 0!important;font-size:.78rem!important}.lps-footer-grid{display:flex!important;flex-direction:column!important;gap:24px!important;padding:32px 16px!important}.lps-footer>div>div{text-align:left!important}h1{font-size:2rem!important}h2{font-size:1.4rem!important}.lps-stats h2,.lps-stats div[style*="2.8rem"]{font-size:2rem!important}aime-quiz{min-height:auto!important}.lps-effect-overlay{padding:0 20px!important}.lps-effect-overlay h2{font-size:1.7rem!important}}</style>`;
      const widgetCss = `<link rel="stylesheet" href="/widgets/style.css">`;
      // Vanilla JS countdown for published scarcity timers
      const timerScript = `<script>(function(){document.querySelectorAll('[data-aime-timer]').forEach(function(t){var end=t.getAttribute('data-end');var redir=t.getAttribute('data-redirect');var msg=t.getAttribute('data-expired-msg')||'This offer has expired.';if(!end)return;var endMs=new Date(end).getTime();var display=t.querySelector('[data-aime-timer-display]');var expiredEl=t.querySelector('[data-aime-timer-expired]');var dEl=t.querySelector('[data-aime-timer-d]');var hEl=t.querySelector('[data-aime-timer-h]');var mEl=t.querySelector('[data-aime-timer-m]');var sEl=t.querySelector('[data-aime-timer-s]');function pad(n){return String(n).padStart(2,'0')}function buildRedirect(base){var ref=document.referrer;if(!ref)return base;return base+(base.indexOf('?')>=0?'&':'?')+'from='+encodeURIComponent(ref)}function tick(){var diff=Math.max(0,Math.floor((endMs-Date.now())/1000));if(diff<=0){if(redir){window.location.href=buildRedirect(redir);return}if(display)display.style.display='none';if(!expiredEl){var p=document.createElement('p');p.style.cssText='font-size:1.1rem;font-style:italic;color:rgba(255,255,255,0.9)';p.textContent=msg;t.appendChild(p)}else{expiredEl.style.display=''}return}var d=Math.floor(diff/86400);var h=Math.floor((diff%86400)/3600);var m=Math.floor((diff%3600)/60);var s=diff%60;if(dEl)dEl.textContent=pad(d);if(hEl)hEl.textContent=pad(h);if(mEl)mEl.textContent=pad(m);if(sEl)sEl.textContent=pad(s)}if(expiredEl)expiredEl.style.display='none';tick();setInterval(tick,1000)})})();<\/script>`;
      // Vanilla JS exit-intent popup for published pages
      const exitScript = `<script>(function(){var ep=document.querySelector('[data-aime-exit-popup]');if(!ep)return;var once=ep.getAttribute('data-once')==='1';var badge=ep.querySelector('[data-aime-exit-badge]');if(badge)badge.remove();var hint=ep.querySelector('[data-aime-exit-hint]');if(hint)hint.remove();var card=ep.querySelector('[data-aime-exit-card]');if(card){card.style.border='none';card.style.borderRadius='16px'}var dismiss=ep.querySelector('button');if(dismiss){dismiss.disabled=false;dismiss.style.cursor='pointer'}ep.style.cssText='display:none;position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,0.6);backdrop-filter:blur(4px);align-items:center;justify-content:center;padding:24px';var shown=false;function show(){if(shown)return;if(once&&sessionStorage.getItem('aime_exit_shown'))return;shown=true;ep.style.display='flex';if(once)sessionStorage.setItem('aime_exit_shown','1')}function hide(){ep.style.display='none'}if(dismiss)dismiss.addEventListener('click',hide);ep.addEventListener('click',function(e){if(e.target===ep)hide()});document.addEventListener('mouseleave',function(e){if(e.clientY<0)show()})})();<\/script>`;
      // Vanilla JS ROI calculator for published pages
      const roiScript = `<script>(function(){document.querySelectorAll('[data-aime-roi]').forEach(function(c){var formula=c.getAttribute('data-formula');if(!formula)return;var inputs=JSON.parse(c.getAttribute('data-inputs')||'[]');var prefix=c.getAttribute('data-output-prefix')||'';var suffix=c.getAttribute('data-output-suffix')||'';var resultEl=c.querySelector('[data-aime-roi-result]');if(!resultEl)return;var vals={};inputs.forEach(function(inp){vals[inp.id]=inp.default||0});function fmt(n){if(isNaN(n)||!isFinite(n))return'\\u2014';return Math.abs(n)>=1000?n.toLocaleString('en-US',{maximumFractionDigits:0}):n.toFixed(2)}function calc(){try{var names=inputs.map(function(i){return i.id});var args=inputs.map(function(i){return vals[i.id]});var fn=new Function(names.join(','),'return ('+formula+')');var r=fn.apply(null,args);resultEl.textContent=prefix+fmt(r)+suffix}catch(e){resultEl.textContent='\\u2014'}}inputs.forEach(function(inp){var el=c.querySelector('[data-aime-roi-input=\"'+inp.id+'\"]');var lbl=c.querySelector('[data-aime-roi-label=\"'+inp.id+'\"]');if(!el)return;el.addEventListener('input',function(){vals[inp.id]=Number(el.value);if(lbl)lbl.textContent='('+inp.prefix+el.value+inp.suffix+')';calc()})});calc()})})();<\/script>`;
      // Hide lead form if a quiz exists (reveal on completion), + iframe auto-resize + input focus
      const quizScript = `<script>(function(){function focusFirstInput(container){if(!container)return;var inp=container.querySelector('input:not([type=hidden])');if(!inp)inp=container.querySelector('textarea');if(inp){setTimeout(function(){inp.readOnly=false;inp.focus()},300)}}var lf=document.getElementById('lead-form');var hf=document.getElementById('hero-form');var cf=document.getElementById('cta-form');var hasQuiz=document.querySelector('aime-quiz')||document.querySelector('iframe[src*="quiz-embed"]');if(hasQuiz&&lf){lf.style.display='none'}window.aimeQuizComplete=function(){setTimeout(function(){if(lf&&lf.style.display==='none')lf.style.display='';var quiz=document.querySelector('aime-quiz')||document.querySelector('iframe[src*="quiz-embed"]');var quizBottom=quiz?quiz.getBoundingClientRect().bottom+window.scrollY:0;var candidates=[lf,hf,cf].filter(Boolean);var below=candidates.filter(function(el){return el.getBoundingClientRect().top+window.scrollY>quizBottom});var target=lf||below[0]||candidates[0];if(target){target.scrollIntoView({behavior:'smooth',block:'start'});focusFirstInput(target)}},5000)};window.addEventListener('message',function(e){if(!e.data||typeof e.data!=='object')return;if(e.data.type==='aime-quiz-resize'){var frames=document.querySelectorAll('iframe');frames.forEach(function(f){if(f.src&&f.src.indexOf(e.data.quizId)>=0){f.style.height=e.data.height+'px'}})};if(e.data.type==='aime-quiz-complete'){window.aimeQuizComplete()}});document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(!a)return;var href=a.getAttribute('href');if(href==='#lead-form'||href==='#hero-form'||href==='#contact'){e.preventDefault();var target=hf||cf||lf;if(target){if(target.style.display==='none')target.style.display='';target.scrollIntoView({behavior:'smooth',block:'start'});focusFirstInput(target)}}else if(href&&href.charAt(0)==='#'&&href.length>1){var t=document.getElementById(href.slice(1));if(t){e.preventDefault();if(window.history&&window.history.pushState){window.history.pushState(null,'',href)}t.scrollIntoView({behavior:'smooth',block:'start'})}}})})();<\/script>`;
      // Vanilla JS testimonial carousel for published pages
      const testimonialScript = `<script>(function(){var wrap=document.querySelector('[data-aime-testimonials]');if(!wrap)return;var cards=Array.prototype.slice.call(wrap.querySelectorAll('[data-testimonial]'));if(cards.length<=1)return;var idx=0;var primary=getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim()||'#2177FF';var dotWrap=wrap.querySelector('[data-aime-testimonial-dots]');function renderDots(){if(!dotWrap)return;dotWrap.innerHTML='';cards.forEach(function(_,i){var d=document.createElement('div');d.style.cssText='height:8px;border-radius:9999px;cursor:pointer;transition:width .3s,background .3s;width:'+(i===idx?'24px':'8px')+';background:'+(i===idx?primary:'#cbd5e1');d.addEventListener('click',function(){goTo(i)});dotWrap.appendChild(d)})}function goTo(next){cards[idx].style.display='none';idx=next%cards.length;cards[idx].style.display='block';cards[idx].style.animation='none';void cards[idx].offsetHeight;cards[idx].style.animation='lp-testimonial-slide-in .45s ease';renderDots()}renderDots();setInterval(function(){goTo(idx+1)},5000)})();<\/script>`;
      // Vanilla JS social proof popup for published pages
      const socialProofScript = `<script>(function(){var sp=document.querySelector('[data-aime-exit-badge]');if(sp)return;var sections=document.querySelectorAll('[style*="dashed"]');sections.forEach(function(s){var badge=s.querySelector('div');if(badge&&badge.textContent&&badge.textContent.indexOf('Social Proof')>=0){var notifications=[];var items=s.querySelectorAll('[style*="gap"]');items.forEach(function(item){var t=item.textContent;if(t)notifications.push(t.trim())});if(notifications.length===0)return;s.style.cssText='display:none';var toast=document.createElement('div');toast.style.cssText='position:fixed;bottom:24px;left:24px;z-index:9998;background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:16px 20px;box-shadow:0 8px 32px rgba(0,0,0,.15);font-size:0.85rem;max-width:320px;opacity:0;transform:translateY(20px);transition:opacity .4s,transform .4s';document.body.appendChild(toast);var ni=0;function show(){toast.textContent=notifications[ni%notifications.length];toast.style.opacity='1';toast.style.transform='translateY(0)';setTimeout(function(){toast.style.opacity='0';toast.style.transform='translateY(20px)'},4000);ni++}setTimeout(show,3000);setInterval(show,8000)}})})();<\/script>`;
      // Vanilla JS hamburger menu toggle for published pages
      const hamburgerScript = `<script>(function(){var btn=document.querySelector('.lps-hamburger');var menu=document.querySelector('.lps-mobile-menu');if(!btn||!menu)return;var open=false;btn.addEventListener('click',function(){open=!open;menu.style.display=open?'flex':'none';btn.innerHTML=open?'<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>':'<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>'});menu.querySelectorAll('a').forEach(function(a){a.addEventListener('click',function(){open=false;menu.style.display='none';btn.innerHTML='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>'})})})();<\/script>`;
      // Apply the brand font on body so sections that don't explicitly set
      // their own font-family inherit it (matches editor canvas behaviour,
      // where canvasRef has style={{fontFamily: brandTheme.fontFamily}}).
      // The serialised innerHTML drops that wrapper style; restore it here.
      // Quote the family name in case it contains spaces (e.g. "Playfair Display").
      const bodyFont = brandTheme.fontFamily && brandTheme.fontFamily !== 'inherit'
        ? `'${brandTheme.fontFamily}',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`
        : `-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      // Inject @font-face for the brand font if a URL is available, so the
      // family above resolves to the actual webfont instead of system fallback.
      const fontFaceCss = buildBrandFontFaceCss(brandTheme);
      const htmlContent = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${pageTitle}</title>${widgetCss}<style>${fontFaceCss}</style>${btnHoverCss}</head><body style="margin:0;font-family:${bodyFont}">${innerHTML}${widgetScript}${effectsScript}${timerScript}${exitScript}${roiScript}${quizScript}${testimonialScript}${socialProofScript}${hamburgerScript}</body></html>`;

      await apiClient.post(`/landing-pages/${landingPageId}/publish`, {
        html_content: htmlContent,
        components,
      });
      navigate('/landing-pages');
    } catch (err: any) {
      alert(getApiError(err, 'Failed to publish landing page. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const selectedComponent =
    components.find((c) => c.id === selectedId) ?? null;

  const existingTypes: LPComponentType[] = components.map((c) => c.type);

  // ── Render guards ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="lp-editor-loading">
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading editor…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lp-editor-error">
        <span>{error}</span>
        <button
          onClick={() => navigate('/landing-pages')}
          style={{
            padding: '8px 16px',
            background: 'transparent',
            border: '1px solid var(--stroke)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            color: 'var(--text)',
            marginTop: '8px',
          }}
        >
          Back to Landing Pages
        </button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="lp-editor">
      {/* ── View-only access banner ── */}
      {saveError && (
        <div style={{
          flexShrink: 0, padding: '8px 16px',
          background: 'var(--warning-50, #FFFBEB)', borderBottom: '1px solid var(--warning-200, #FDE68A)',
          color: 'var(--warning-800, #92400E)', fontSize: '0.82rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
          <span>{saveError}</span>
          <button onClick={() => setSaveError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '2px 4px', fontSize: '1rem', lineHeight: 1 }} title="Dismiss">×</button>
        </div>
      )}
      {/* ── Top bar ── */}
      <div className="lp-editor-topbar">
        <button
          onClick={() => navigate('/landing-pages')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-secondary)',
            fontSize: '0.875rem',
            padding: '4px 8px',
            borderRadius: 'var(--radius-md)',
          }}
          title="Back to landing pages"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="lp-editor-topbar-sep" />

        {editingTitle ? (
          <input
            className="lp-editor-topbar-title-input"
            value={titleDraft}
            autoFocus
            disabled={savingTitle}
            onChange={(e) => setTitleDraft(e.target.value)}
            onFocus={(e) => e.target.select()}
            onBlur={commitTitleEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTitleEdit(); }
              else if (e.key === 'Escape') { e.preventDefault(); setEditingTitle(false); }
            }}
          />
        ) : (
          <button
            type="button"
            className="lp-editor-topbar-title"
            onClick={startTitleEdit}
            disabled={lockedLayout}
            title={lockedLayout ? pageTitle : 'Click to rename'}
          >
            <span className="lp-editor-topbar-title-text">{pageTitle}</span>
            {!lockedLayout && <Pencil size={13} className="lp-editor-topbar-title-pencil" />}
          </button>
        )}

        {/* Page settings cog */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setSettingsOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '32px', height: '32px',
              background: settingsOpen ? 'var(--primary-50, #EAF3FF)' : 'transparent',
              border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)',
              cursor: 'pointer', color: 'var(--text-secondary)',
            }}
            title="Page settings"
          >
            <Settings size={16} />
          </button>
          {settingsOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: '6px',
              background: 'var(--panel)', border: '1px solid var(--stroke)',
              borderRadius: '8px', padding: '12px 14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100,
              minWidth: '200px', fontSize: '0.82rem',
            }}>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: '10px' }}>Page Settings</div>
              <div style={{ marginBottom: '6px', color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500 }}>Button Style</div>
              {([
                { value: 'pill', label: 'Pill (fully rounded)' },
                { value: 'rounded', label: 'Rounded corners' },
                { value: 'square', label: 'Square (no radius)' },
              ] as const).map(opt => (
                <label key={opt.value} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 0', cursor: 'pointer', color: 'var(--text)',
                }}>
                  <input
                    type="radio"
                    name="btnRadius"
                    checked={pageSettings.buttonRadius === opt.value}
                    onChange={() => {
                      setPageSettings(s => ({ ...s, buttonRadius: opt.value }));
                      // Persist to hero component props
                      setComponents(prev => prev.map(c => c.type === 'lp_hero' ? { ...c, props: { ...c.props, _page_button_radius: opt.value } } : c));
                      markDirty();
                    }}
                    style={{ accentColor: 'var(--primary-500, #2177FF)' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Save indicator */}
        <span
          className={`lp-save-indicator${saving ? ' saving' : isDirty ? ' dirty' : ''}`}
        >
          {saving ? 'Saving…' : isDirty ? 'Unsaved changes' : 'Saved'}
        </span>

        <div className="lp-editor-topbar-sep" />

        <div className="lp-editor-topbar-actions">
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              background: 'var(--panel-strong)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              cursor: saving || !isDirty ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: 'var(--text)',
              opacity: saving || !isDirty ? 0.5 : 1,
            }}
            title="Save changes"
          >
            <Save size={15} />
            Save
          </button>

          <button
            onClick={handlePreview}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              background: 'var(--panel-strong)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: 'var(--text)',
            }}
            title="Preview page"
          >
            <Eye size={15} />
            Preview
          </button>

          <button
            onClick={handlePublish}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 16px',
              background: 'var(--primary-500, #2177FF)',
              border: 'none',
              borderRadius: 'var(--radius-full, 9999px)',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#ffffff',
              opacity: saving ? 0.7 : 1,
            }}
            title="Publish page"
          >
            <Upload size={15} />
            Publish
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="lp-editor-body">
        {/* Left panel */}
        <div className="lp-editor-left">
          <div className="lp-editor-left-heading">Sections</div>

          {!lockedLayout && (
          <button
            className="lp-editor-add-btn"
            onClick={() => setBrowserOpen(true)}
            disabled={!!maxComponents && components.length >= maxComponents}
          >
            <Plus size={14} />
            Add Section
          </button>
          )}

          {components.length > 0 && (
            <nav className="lp-editor-section-nav">
              {components.map((comp) => {
                const palette = LP_COMPONENT_PALETTE.find(
                  (p) => p.type === comp.type
                );
                return (
                  <button
                    key={comp.id}
                    className={`lp-editor-section-nav-item${
                      selectedId === comp.id ? ' active' : ''
                    }`}
                    onClick={() => scrollToSection(comp.id)}
                    title={palette?.label ?? comp.type}
                  >
                    {palette?.label ?? comp.type}
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        {/* Canvas */}
        <div className="lp-editor-canvas" onClick={(e) => {
          const a = (e.target as HTMLElement).closest('a');
          if (a && a.getAttribute('href')?.startsWith('#')) {
            e.preventDefault();
            const targetId = a.getAttribute('href')!.slice(1);
            const el = canvasRef.current?.querySelector(`#${targetId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}>
          <div className="lp-editor-canvas-inner" ref={canvasRef} style={brandTheme.fontFamily && brandTheme.fontFamily !== 'inherit' ? { fontFamily: brandTheme.fontFamily } : undefined}>
            {components.length === 0 ? (
              <div className="lp-canvas-empty">
                <div className="lp-canvas-empty-icon">◻</div>
                <p className="lp-canvas-empty-text">No sections yet</p>
                <p className="lp-canvas-empty-sub">
                  Click{' '}
                  <strong>Add Section</strong> in the left panel to build
                  your page.
                </p>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="lp-canvas">
                  {(droppableProvided) => (
                    <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                      {components.map((comp, idx) => (
                        <Draggable key={comp.id} draggableId={comp.id} index={idx}>
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                            >
                              <SectionWrapper
                                component={comp}
                                isSelected={selectedId === comp.id}
                                isFirst={idx === 0}
                                isLast={idx === components.length - 1}
                                onClick={() => setSelectedId(comp.id)}
                                onMoveUp={() => moveUp(comp.id)}
                                onMoveDown={() => moveDown(comp.id)}
                                onDuplicate={() => duplicateComponent(comp.id)}
                                onDelete={() => deleteComponent(comp.id)}
                                onRegenerate={handleRegenerateSection}
                                onPropsChange={handlePropsChange}
                                dragHandleProps={draggableProvided.dragHandleProps ?? undefined}
                                brandTheme={brandTheme}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="lp-editor-right">
          <LPPropertiesPanel
            component={selectedComponent}
            onChange={handlePropsChange}
            projectId={projectId}
            brandTheme={brandTheme}
            allComponents={components}
          />
        </div>
      </div>

      {/* Component browser overlay */}
      <LPComponentBrowser
        isOpen={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onAdd={addComponent}
        existingTypes={existingTypes}
      />

      {/* Inline text formatting toolbar */}
      <LPInlineFloatingToolbar />
    </div>
  );
};

export default LandingPageEditor;
